import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import ScrollToTop from '@/components/base/ScrollToTop';
import { categories } from '@/mocks/categories';
import { getArticleBySlug, getRelatedArticles } from '@/mocks/articles';
import { siteInfo } from '@/mocks/site';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;
  const relatedArticles = article ? getRelatedArticles(article) : [];
  const category = article ? categories.find((c) => c.slug === article.category) : undefined;

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <div className="min-h-screen bg-background-50">
          <Navbar />
          <div className="pt-20 text-center py-20">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-amber-50 flex items-center justify-center">
              <i className="ri-error-warning-line text-2xl text-amber-400"></i>
            </div>
            <p className="text-foreground-500 text-sm">文章未找到</p>
            <Link to="/" className="mt-4 inline-flex items-center gap-2 text-amber-600 hover:text-amber-700 text-sm font-medium">
              <i className="ri-arrow-left-line"></i> 返回首页
            </Link>
          </div>
          <Footer />
        </div>
      </>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Organization',
      name: article.author,
    },
    datePublished: article.date,
    image: article.imageUrl,
    publisher: {
      '@type': 'Organization',
      name: siteInfo.name,
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${siteInfo.url}/article/${slug}`,
    },
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={article.title}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`${siteInfo.url}/article/${slug}`}
        ogType="article"
        ogImage={article.imageUrl}
        schema={articleSchema}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <div className="pt-20 bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-6 pb-10">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: category?.name || '', href: `/category/${article.category}` },
                { label: article.title },
              ]}
            />

            <article>
              <div className="mb-6">
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="text-xs text-amber-600 font-medium bg-amber-50 px-3 py-1 rounded-full">
                    {category?.name}
                  </span>
                  <span className="text-xs text-foreground-400">{article.date}</span>
                  <span className="text-xs text-foreground-400">{article.readTime}</span>
                </div>
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 leading-tight mb-4">
                  {article.title}
                </h1>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
                    <i className="ri-user-line text-sm text-amber-600"></i>
                  </div>
                  <span className="text-sm text-foreground-600">{article.author}</span>
                </div>
              </div>

              <div className="aspect-[21/9] rounded-xl overflow-hidden mb-8">
                <img
                  src={article.imageUrl}
                  alt={article.title}
                  className="w-full h-full object-cover"
                />
              </div>

              <div
                className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              <div className="mt-8 pt-6 border-t border-background-200/70 flex flex-wrap gap-2">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs bg-background-100 text-foreground-500 px-3 py-1 rounded-full"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </article>

            <div className="mt-12 pt-10 border-t border-background-200/70">
              <h3 className="text-lg font-semibold text-foreground-950 mb-6">相关文章推荐</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {relatedArticles.map((rel) => (
                  <Link
                    key={rel.slug}
                    to={`/article/${rel.slug}`}
                    className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-amber-400/50 transition-all duration-300 cursor-pointer"
                  >
                    <div className="aspect-[16/10] overflow-hidden">
                      <img
                        src={rel.imageUrl}
                        alt={rel.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <span className="text-xs text-amber-600 font-medium bg-amber-50 px-2 py-0.5 rounded-full">
                        {categories.find((c) => c.slug === rel.category)?.name}
                      </span>
                      <h4 className="mt-2 text-sm font-semibold text-foreground-950 line-clamp-2 leading-snug">
                        {rel.title}
                      </h4>
                      <p className="text-xs text-foreground-400 mt-2">{rel.date}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>

        <Footer />
      </div>
    </>
  );
}